import { Badge, Group, Stack, Text, Tooltip } from "@mantine/core";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { COLLECTIONS } from "@/firestore/collections";
import { useFirestoreDoc } from "@/firestore/hooks";
import type { SmartAccountOperationDoc, WalletMigrationDoc } from "@/firestore/types";
import { formatTimestamp } from "@/lib/datetime";
import { useNow } from "@/lib/useNow";
import {
  AUTOMATIC_ACTION_MAX_AGE_MS,
  automaticActionsActive,
  isActiveMigration,
  stallReason,
  stallStatus,
  waitingOn,
} from "@/wallet/migrations";
import { RelatedLink } from "./RelatedLink";
import { StatusBadge } from "./StatusBadge";
import { Timestamp } from "./Timestamp";

dayjs.extend(relativeTime);

/** "Stalled 2 hours" (red) once a row is past `stalledAfter`; nothing before. */
export function MigrationStallChip({
  migration,
  now,
}: {
  migration: Pick<WalletMigrationDoc, "state" | "updatedAt"> & Partial<WalletMigrationDoc>;
  now: number;
}) {
  const stall = stallStatus(migration, now);
  if (!stall.stalled || !stall.stalledAfter) return null;
  const who = waitingOn(migration);
  return (
    <Tooltip
      label={`Past its stall threshold since ${formatTimestamp(stall.stalledAfter)}${
        stall.estimated ? " (estimated: the document predates stalledAfter)" : ""
      }${who ? `. Waiting on the ${who}.` : "."}`}
      multiline
      w={300}
      withArrow
    >
      <Badge color="red" variant="light" radius="sm" tt="none">
        Stalled {dayjs(stall.stalledAfter).from(now, true)}
      </Badge>
    </Tooltip>
  );
}

/** State badge plus the stall chip. */
export function MigrationStateCell({
  migration,
  now,
}: {
  migration: Pick<WalletMigrationDoc, "state" | "updatedAt"> & Partial<WalletMigrationDoc>;
  now: number;
}) {
  return (
    <Group gap={4} wrap="nowrap">
      <StatusBadge state={migration.state} />
      <MigrationStallChip migration={migration} now={now} />
    </Group>
  );
}

/** The migration detail's "Stalled after" field: the instant, the chip, and whether it is estimated. */
export function MigrationStallField({
  migration,
}: {
  migration: Pick<WalletMigrationDoc, "state" | "updatedAt"> & Partial<WalletMigrationDoc>;
}) {
  const now = useNow();
  const stall = stallStatus(migration, now);
  if (!stall.stalledAfter) return <Text c="dimmed">— (this state never stalls)</Text>;
  return (
    <Group gap="xs" wrap="nowrap">
      <Timestamp value={stall.stalledAfter} absolute />
      <MigrationStallChip migration={migration} now={now} />
      {stall.estimated ? (
        <Text size="xs" c="dimmed">
          (estimated)
        </Text>
      ) : null}
    </Group>
  );
}

/** A transfer leg's smart-account operation, with its live state (admin-only SAO document). */
export function TransferLegField({ saoId }: { saoId: string | null | undefined }) {
  const sao = useFirestoreDoc<SmartAccountOperationDoc>(
    COLLECTIONS.smartAccountOperations,
    saoId ?? null,
  );
  if (!saoId) return <Text c="dimmed">—</Text>;
  return (
    <Group gap="xs" wrap="nowrap">
      <RelatedLink entity="smartAccountOperation" id={saoId} />
      {sao.data ? <StatusBadge state={sao.data.state} /> : null}
    </Group>
  );
}

/** The stale sweep's verdict on the row: reason, and until when it acts by itself. */
export function MigrationSweepField({
  migration,
}: {
  migration: Pick<WalletMigrationDoc, "state" | "updatedAt"> & Partial<WalletMigrationDoc>;
}) {
  const now = useNow();
  const reason = stallReason(migration);
  const acting = automaticActionsActive(migration, now);
  const estimated = migration.automaticActionsUntil === undefined;
  const until =
    migration.automaticActionsUntil ??
    (Date.parse(migration.updatedAt)
      ? new Date(Date.parse(migration.updatedAt) + AUTOMATIC_ACTION_MAX_AGE_MS).toISOString()
      : null);
  const sweepScans =
    isActiveMigration(migration) ||
    (migration.state === "completed" && !migration.scheduledCleanupAt);
  return (
    <Stack gap={2}>
      <Text size="sm">{reason ? reason.label : "—"}</Text>
      {sweepScans ? (
        <Group gap={6} wrap="nowrap">
          <Badge color={acting ? "green" : "orange"} variant="light" radius="sm" tt="none">
            {acting ? "Sweep acting" : "Operator only"}
          </Badge>
          <Text size="xs" c="dimmed">
            {acting ? "until" : "since"} <Timestamp value={until} />
            {estimated ? " (estimated)" : ""}
          </Text>
        </Group>
      ) : null}
    </Stack>
  );
}
